<template>
	<a
		ref="rootElement"
		class="hotel-list-item"
		:class="{ 'is-favorite-page': isFavoriteList }"
		:href="addEnabledParams(item.Hotel.URL)"
	>
		<div class="hotel-list-item__image">
			<HotelImageSlider
				v-if="item.NumberOfImages && item.NumberOfImages > 0"
				:iff-code="item.Hotel.IffCode"
				:alt="item.Hotel.Name"
				:number-of-images="item.NumberOfImages"
				class="hotel-list-item__slider"
				:item-index="itemIndex"
			/>
			<img
				v-else
				class="hotel-list-item__slider"
				src="data:image/gif;base64,R0lGODdhEgAMAIABAPD2/////ywAAAAAEgAMAAACDYSPqcvtD6OctNqLVQEAOw=="
				:alt="item.Hotel.Name"
			/>
			<BookmarkHotelButton
				:item="item"
				class="hotel-list-item__bookmark-icon"
				@click.prevent
			/>
		</div>
		<div
			:class="{ 'is-favorite-list': isFavoriteList }"
			class="hotel-list-item__body"
		>
			<HotelListItemHeader
				class="hotel-list-item__header"
				:hotel="item.Hotel"
				:location="item.Location"
				:show-region="showRegion"
				:show-suns="showSuns"
			/>
			<HotelListItemReview
				v-if="review.NumberOfRatings !== undefined && review.Ratings !== undefined && review.NumberOfRatings > 0"
				:review
				class="hotel-list-item__review"
			/>
			<HotelListItemFeatures
				v-if="item.PopularAttributes && item.PopularAttributes.length !== 0"
				class="hotel-list-item__features"
				:popular-attributes="item.PopularAttributes"
			/>
			<template v-if="!isFavoriteList">
				<HotelListItemStorno
					:offer-feature="hasOfferFeatures"
					class="hotel-list-item__storno"
					@click.prevent
				/>
				<HotelListItemOffer
					v-if="offer"
					class="hotel-list-item__offer"
					:href="`${addEnabledParams(item.Hotel.URL)}#offers`"
					:offer="offer"
					:discount="discount"
				/>
			</template>
			<HotelListItemFavouriteDetails
				v-else
				details="16.6. - 05.7., 1 Woche, 2 Erw., 1 Kind, inkl. Flug ab Berlin Brandenburg lorem ipsum very lonk text such text very information"
				class="hotel-list-item__favorite-details"
			/>
		</div>
	</a>
</template>

<script setup lang="ts">
import BookmarkHotelButton from '@lmt-rpb/BookmarkHotelButton/BookmarkHotelButton.vue';
import HotelImageSlider from '@lmt-rpb/HotelImageSlider/HotelImageSlider.vue';
import HotelListItemOffer from '@lmt-rpb/HotelListItemOffer/HotelListItemOffer.vue';
import HotelListItemReview from '@lmt-rpb/HotelListItemReview/HotelListItemReview.vue';
import HotelListItemHeader from '@lmt-rpb/HotelListItemHeader/HotelListItemHeader.vue';
import HotelListItemFeatures from '@lmt-rpb/HotelListItemFeatures/HotelListItemFeatures.vue';
import HotelListItemStorno from '@lmt-rpb/HotelListItemStorno/HotelListItemStorno.vue';

import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { addEnabledParams } from '@/components/common/services/url';
import { flexOptions } from '@/js/data/flex-options';

import type { HotelListItemType } from '@/interfaces/hotel-list-types/hotel-list-types';
import type { DiscountHotelPriceOffer } from '@/interfaces/neusta-discount-price';
import HotelListItemFavouriteDetails from '@lmt-rpb/HotelListItemFavouriteDetails/HotelListItemFavouriteDetails.vue';

type Props = {
	item: HotelListItemType;
	showSuns?: boolean;
	isFavoriteList?: boolean;
	hotelListType: string;
	itemIndex?: number;
	discount?: DiscountHotelPriceOffer | undefined;
};

const rootElement: Ref<HTMLElement | undefined> = ref();

const props = withDefaults(defineProps<Props>(), { showSuns: false, itemIndex: 1, discount: undefined });

const showRegion = computed(() => props.hotelListType === 'COUNTRY' || props.hotelListType === 'REGIONGROUP');

const review = computed(() => {
	const { Ratings, NumberOfRatings } = props.item;
	return { Ratings, NumberOfRatings };
});

const offer = computed(() => {
	if (props.item.PackageOffer) {
		return {
			...props.item.PackageOffer,
			Pricing: props.item.Pricing,
		};
	}
	if (props.item.AccommodationOffer) {
		return {
			...props.item.AccommodationOffer,
			Pricing: props.item.Pricing,
		};
	}
	return null;
});

const hasOfferFeatures = computed(() => {
	const offerFeatures = props.item.OfferFeatures;
	if (!offerFeatures) return false;

	return flexOptions.some((key) => typeof offerFeatures[key] === 'string' && offerFeatures[key]?.trim() !== '');
});
</script>

<style lang="scss" scoped>
$image-desktop-width: 27rem;
$container-desktop-height: 21.739rem;
$container-desktop-height-favorite-page: 18.8rem;
$image-tablet-width: 25.5rem;
$container-tablet-height: 25.5rem;

.hotel-list-item {
	margin-inline: auto;
	box-shadow: $box-shadow;
	background-color: $color-white;
	width: 100%;
	max-width: 40rem;
	border-radius: $border-radius-large;
	cursor: default;
	position: relative;
	color: unset;
	text-decoration: none;
	display: block;

	@include focus-visible-state($border-radius-large);

	&:hover,
	&:focus,
	&:visited,
	&:active {
		text-decoration: none;
		cursor: pointer;
	}

	&__image {
		border-top-left-radius: $border-radius-large;
		border-top-right-radius: $border-radius-large;
		position: relative;
		overflow: hidden;

		@media (min-width: $breakpoint-small) {
			border-bottom-left-radius: $border-radius-large;
			border-top-right-radius: 0;
		}
	}

	&__slider {
		width: 100%;
		height: 100%;

		:deep(swiper-container::part(button-next)),
		:deep(swiper-container::part(button-prev)) {
			color: $color-text-label;
			width: 3rem;
			height: 3rem;
			transform: translateY(50%);

			&::after {
				font-size: $font-small-2;
				font-weight: 900;
				width: 0.8rem;
				display: inline-block;
				margin: auto;
			}
		}

		:deep(swiper-container::part(button-next)) {
			padding-left: 0.25rem;
		}

		:deep(swiper-container::part(button-prev)) {
			padding-right: 0.25rem;
		}

		:deep(.hotel-image-slider__container-image) {
			border-radius: $border-radius-large $border-radius-large 0 0;
			margin: 0;
			width: 100%;
			height: 100%;
		}

		:deep(.hotel-image-slider__image) {
			width: 100%;
			height: auto;
			aspect-ratio: 3/2;
			object-fit: cover;
		}
	}

	&__bookmark-icon {
		position: absolute;
		top: 1.6rem;
		right: 1rem;
		z-index: 1;
		width: 2.4rem;
		height: 2.4rem;

		&:not(.bookmark-hotel-button--active) :deep(.bookmark-hotel-button__svg) {
			fill: hsl(0deg 0% 0% / 19%);
		}

		:deep(.bookmark-hotel-button__svg) {
			stroke: #fff;
			stroke-width: 0.125rem;
			overflow: visible;
		}

		:deep(.bookmark-hotel-button__heart-icon) {
			width: 2.4rem;
			height: 2.4rem;
		}
	}

	&__body {
		padding: 0.8rem 1.6rem 1.6rem;
	}

	&__header {
		margin-bottom: 0.8rem;
	}

	&__review {
		margin-bottom: 1.2rem;
	}
	&__offer {
		display: block;
		padding-inline: 0.45rem;
	}

	&__favorite-details,
	&__offer {
		border-top: solid 0.1rem $color-border;
		margin-top: 1.2rem;
		padding-top: 0.8rem;
	}

	&__storno {
		margin-top: 1rem;
		grid-area: storno;
	}
}
// Note: maybe an upgrade needed in scss, current version does not resolve scss variables in container queries
// a bit smaller than $breakpoint-small, it's the width of the tablet component in the design
@container hotel_item_list (min-width: 72.5rem) {
	.hotel-list-item {
		display: grid;
		grid-template-columns: minmax(0, $image-tablet-width) minmax(0, 1fr);
		width: 100%;
		max-width: 100cqw;
		min-height: $container-tablet-height;

		&__image {
			height: 100%;
			width: 100%;
		}

		&__slider {
			position: absolute;
			inset: 0;

			:deep(swiper-container::part(button-next)),
			:deep(swiper-container::part(button-prev)) {
				color: $color-text-label;
				width: 4rem;
				height: 4rem;
				transform: none;

				&::after {
					font-size: $font-large-4;
					font-weight: 900;
					width: 1.4rem;
				}
			}

			:deep(swiper-container::part(button-next)) {
				padding-left: 0.5rem;
			}

			:deep(swiper-container::part(button-prev)) {
				padding-right: 0.5rem;
			}

			:deep(.hotel-image-slider__container-image) {
				border-radius: $border-radius-large 0 0 $border-radius-large;
			}

			:deep(.hotel-image-slider__image) {
				height: 100%;
			}
		}

		&__bookmark-icon {
			width: auto;
			height: auto;

			:deep(.bookmark-hotel-button__heart-icon) {
				width: 3.407rem;
				height: 3.298rem;
			}
		}

		&__body {
			display: grid;
			row-gap: 1.4rem;
			padding: 1.6rem 2.4rem 1.6rem 1.6rem;

			&.is-favorite-list {
				grid-template:
					'header header header' minmax(0, 1fr)
					'review review offer' minmax(0, 1fr)
					'features features offer' minmax(0, 1fr)
					/ minmax(0, 1fr) minmax(0, 1fr) minmax(18rem, 1fr);
			}
			grid-template:
				'header header header' minmax(0, 1fr)
				'review review offer' minmax(0, 1fr)
				'features features offer' minmax(0, 1fr)
				'storno storno offer' minmax(0, 0.32fr)
				/ minmax(0, 1fr) minmax(0, 1fr) minmax(18rem, 1fr);
			align-items: center;
		}

		&__review {
			grid-area: review;
			margin-bottom: 0;
		}

		&__features {
			grid-area: features;
		}

		&__favorite-details,
		&__offer {
			margin-top: 0;
			padding-top: 0;
			border-top: none;
			height: 100%;
			grid-area: offer;
		}

		&__storno {
			margin-top: 0;
		}

		&__header {
			grid-area: header;
			margin-bottom: 0;
		}
	}
}

// $breakpoint-medium - for some reason the scss variable doesn't get resolved
@container hotel_item_list (min-width: 90rem) {
	.hotel-list-item {
		grid-template-columns: minmax(0, $image-desktop-width) minmax(0, 1fr);
		max-width: 105.4rem;
		min-height: $container-desktop-height;

		&.is-favorite-page {
			min-height: $container-desktop-height-favorite-page;
		}

		&__body {
			&.is-favorite-list {
				grid-template:
					'header header offer' minmax(0, 7rem)
					'review features offer' minmax(0, 1fr)
					'review features offer' minmax(0, 0.3fr)
					/ minmax(0, 1fr) minmax(0, 1fr) minmax(18rem, 1fr);
			}
			grid-template:
				'header header offer' minmax(0, 7rem)
				'review features offer' minmax(0, 1fr)
				'storno features offer' minmax(0, 0.3fr)
				/ minmax(22.5rem, 1fr) minmax(0, 1fr) minmax(min-content, 1fr);
			gap: 2rem 2.7rem;
			align-items: start;
		}

		&__favorite-details,
		&__offer {
			border-left: solid 1px $color-border;
			align-items: flex-end;
			padding: 0;
		}
	}
}

// Here is a lot of c/p'd code from the @container declarations above.
// This is necessary, because Mozilla Firefox processes @container differently (not at all)
// for Print Previews in comparison to Chrome and Edge
// Once Mozilla decides to change this we can simplify this section
@media print {
	.hotel-list-item {
		display: grid;
		grid-template-columns: minmax(0, $image-desktop-width) minmax(0, 1fr);
		width: 100%;
		min-height: $container-desktop-height;
		&.is-favorite-page {
			min-height: $container-desktop-height-favorite-page;
		}
		border: 0.2rem solid $color-hotellist-item-border-print;
		page-break-inside: avoid;
		break-inside: avoid;

		&__image {
			height: 100%;
			width: 100%;
			border-bottom-left-radius: $border-radius-image;
			border-top-left-radius: $border-radius-image;
			border-top-right-radius: 0;
		}

		&__body {
			grid-template:
				'header header offer' minmax(0, 7rem)
				'review features offer' minmax(0, 1fr)
				'storno features offer' minmax(0, 0.3fr)
				/ minmax(22.5rem, 1fr) minmax(0, 1fr) minmax(min-content, 1fr);
			gap: 2rem 2.7rem;
			align-items: start;

			display: grid;
			row-gap: 1.4rem;
			padding: 1.6rem 2.4rem 1.6rem 1.6rem;
			min-width: 73rem; // body-width necessary for print mode
		}

		&__slider {
			position: absolute;
			inset: 0;

			:deep(swiper-container::part(button-next)),
			:deep(swiper-container::part(button-prev)) {
				color: $color-text-label;
				width: 4rem;
				height: 4rem;
				transform: none;

				&::after {
					font-size: $font-large-4;
					font-weight: 900;
					width: 1.4rem;
				}
			}

			:deep(swiper-container::part(button-next)) {
				padding-left: 0.5rem;
			}

			:deep(swiper-container::part(button-prev)) {
				padding-right: 0.5rem;
			}

			:deep(.hotel-image-slider__container-image) {
				border-radius: $border-radius-large 0 0 $border-radius-large;
			}

			:deep(.hotel-image-slider__image) {
				height: 100%;
			}
		}

		&__offer {
			border-left: solid 1px $color-border;
			align-items: flex-end;
			padding: 0;
			margin-top: 0;
			border-top: none;
			height: 100%;
			grid-area: offer;
		}

		&__review {
			grid-area: review;
			margin-bottom: 0;
		}

		&__features {
			grid-area: features;
		}

		&__storno {
			margin-top: 0;
		}

		&__header {
			grid-area: header;
			margin-bottom: 0;
		}

		:deep(.hotel-image-slider__custom-next-button) {
			display: none;
		}
		:deep(.bookmark-hotel-button) {
			display: none;
		}
	}
}
</style>
